import { useState, useEffect, useRef } from "react";
import config from "../../../../config.json";
import imgVide from "../../../assets/images/vide.png";
import { useNavigate } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import {
  hideMenu,
  updateMenusOrder,
  toggleMenuEnabled,
} from "../../../services/MenuService";
import { exportMenuBackupZip } from "../../../services/menuExportExcel";
import { fetchRestoDataByUser } from "../../../services/restaurantService";
import { confirmArchive, confirmReorder } from "../../../utils/confirmDialogs";
import MenuShareModal from "./MenuShareModal";
import useDragAutoScroll from "./MenuTree/useDragAutoScroll";
import { toast } from "react-toastify";
import "../../../i18n";
import { useTranslation } from "react-i18next";
import "./menusList.css";
import "../Dashboard/kitchenLuxe.css";

const FILTER_ALL = "all";
const FILTER_ENABLED = "enabled";
const FILTER_DISABLED = "disabled";

const SORT_NEWEST = "newest";
const SORT_OLDEST = "oldest";
const SORT_NAME_ASC = "name-asc";
const SORT_NAME_DESC = "name-desc";
const SORT_CUSTOM = "custom";

const NO_DRAG_SELECTOR =
  ".menu-list-actions, .menu-list-visibility-row, .dropdown-menu, button, a, input, .dropdown-toggle";

function getMenuName(menu) {
  return String(menu?.menuDetails?.[0]?.menuName || "").trim().toLowerCase();
}

function getMenuCreatedAt(menu) {
  const raw = menu?.createdAt || menu?.created_at || menu?._id;
  const time = new Date(raw).getTime();
  return Number.isFinite(time) ? time : 0;
}

function sortMenusList(list, sortMode) {
  const next = [...list];
  if (sortMode === SORT_CUSTOM) return next;
  if (sortMode === SORT_NEWEST) {
    return next.sort((a, b) => getMenuCreatedAt(b) - getMenuCreatedAt(a));
  }
  if (sortMode === SORT_OLDEST) {
    return next.sort((a, b) => getMenuCreatedAt(a) - getMenuCreatedAt(b));
  }
  if (sortMode === SORT_NAME_ASC) {
    return next.sort((a, b) => getMenuName(a).localeCompare(getMenuName(b)));
  }
  if (sortMode === SORT_NAME_DESC) {
    return next.sort((a, b) => getMenuName(b).localeCompare(getMenuName(a)));
  }
  return next;
}

const MenuList = ({ menusResponse, searchTerm = "" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [menus, setMenus] = useState(menusResponse);
  const [filter, setFilter] = useState(FILTER_ALL);
  const [sortMode, setSortMode] = useState(SORT_NEWEST);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const draggedIndexRef = useRef(null);
  const [exportingMenuId, setExportingMenuId] = useState(null);
  const [togglingMenuId, setTogglingMenuId] = useState(null);
  const [shareMenu, setShareMenu] = useState(null);
  const [restaurantData, setRestaurantData] = useState(null);
  // Native HTML5 drag: scroll the page when the dragged card nears the top/bottom edge.
  const autoScroll = useDragAutoScroll({ moveEvent: "dragover" });

  const canDragReorder = sortMode === SORT_CUSTOM;

  useEffect(() => {
    setMenus(menusResponse);
  }, [menusResponse]);

  useEffect(() => {
    const loadRestaurant = async () => {
      try {
        const data = await fetchRestoDataByUser();
        setRestaurantData(data);
      } catch (error) {
        console.error("Error loading restaurant for menu share:", error);
      }
    };
    loadRestaurant();
  }, []);

  const query = String(searchTerm || "").trim().toLowerCase();

  const filteredMenus = sortMenusList(
    menus.filter((menu) => {
      if (filter === FILTER_ENABLED && menu.isEnabled === false) return false;
      if (filter === FILTER_DISABLED && menu.isEnabled !== false) return false;
      if (!query) return true;
      return (menu.menuDetails || []).some((detail) =>
        String(detail?.menuName || "")
          .toLowerCase()
          .includes(query)
      );
    }),
    sortMode
  );

  const handleArchiveMenu = async (id) => {
    if (!(await confirmArchive(t, "archiveConfirmMenu"))) return;
    try {
      await hideMenu(id);
      setMenus((prevMenus) => prevMenus.filter((menu) => menu._id !== id));
    } catch (error) {
      console.error("Erreur lors de l'archivage du menu :", error);
    }
  };

  const handleToggleEnabled = async (menu) => {
    if (togglingMenuId) return;
    const nextEnabled = !(menu.isEnabled !== false);
    setTogglingMenuId(menu._id);
    try {
      await toggleMenuEnabled(menu._id, nextEnabled);
      setMenus((prev) =>
        prev.map((m) => (m._id === menu._id ? { ...m, isEnabled: nextEnabled } : m))
      );
      toast.success(
        nextEnabled
          ? (t("menuEnabled") || "Menu is now visible to customers.")
          : (t("menuDisabled") || "Menu is now hidden from customers.")
      );
    } catch (err) {
      toast.error(t("errorUpdating") || "Error updating menu.");
    } finally {
      setTogglingMenuId(null);
    }
  };

  /**
   * Move `fromId` next to `toId` and return the new full order.
   * In a sorted view the base is the order currently on screen, so the drop
   * lands exactly where the user put it and becomes the new custom order.
   */
  const reorderMenus = (fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return null;
    const base = sortMode === SORT_CUSTOM ? [...menus] : sortMenusList(menus, sortMode);
    const fromIndex = base.findIndex((m) => m._id === fromId);
    const toIndex = base.findIndex((m) => m._id === toId);
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return null;
    const [removed] = base.splice(fromIndex, 1);
    base.splice(toIndex, 0, removed);
    return base;
  };

  const handleDragStart = (e, menuId) => {
    // Images / controls must not steal the card drag.
    if (e.target?.closest?.(NO_DRAG_SELECTOR)) {
      e.preventDefault();
      return;
    }
    draggedIndexRef.current = menuId;
    setDraggedIndex(menuId);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(menuId));
    autoScroll.onDragStart();
  };

  const handleDragOver = (e, menuId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverIndex !== menuId) setDragOverIndex(menuId);
  };

  const handleDragLeave = (e) => {
    // Ignore leave events fired when moving between children of the same card.
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragOverIndex(null);
  };

  const handleDrop = async (e, dropMenuId) => {
    e.preventDefault();
    autoScroll.onDragEnd();
    setDragOverIndex(null);
    const fromData = e.dataTransfer.getData("text/plain") || null;
    const fromId = draggedIndexRef.current != null ? draggedIndexRef.current : fromData;
    draggedIndexRef.current = null;
    setDraggedIndex(null);
    const reordered = reorderMenus(fromId, dropMenuId);
    if (!reordered) return;
    if (!(await confirmReorder(t))) return;
    const previousMenus = menus;
    const previousSort = sortMode;
    setMenus(reordered);
    // A drop defines the customer order, so the view follows it.
    setSortMode(SORT_CUSTOM);
    try {
      await updateMenusOrder(reordered.map((m) => m._id));
      toast.success(t("menusOrderUpdated") || "Menus order updated. Order will reflect on the client.");
    } catch (err) {
      setMenus(previousMenus);
      setSortMode(previousSort);
      toast.error(t("errorUpdating") || "Error updating order.");
    }
  };

  const handleDragEnd = () => {
    autoScroll.onDragEnd();
    draggedIndexRef.current = null;
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleExportExcel = async (menu) => {
    if (exportingMenuId) return;
    try {
      setExportingMenuId(menu._id);
      await exportMenuBackupZip(
        menu._id,
        menu.menuDetails?.[0]?.menuName || "menu"
      );
      toast.success(
        t("exportSuccess") ||
          "Backup downloaded: Excel + images folder (ZIP)."
      );
    } catch (err) {
      console.error("Menu export failed:", err);
      toast.error(
        t("exportFailed") ||
          "Could not export menu. Please try again or check your connection."
      );
    } finally {
      setExportingMenuId(null);
    }
  };

  return (
    <>
      <div className="menu-list-toolbar">
        <p className="menu-list-toolbar-label">{t("filterBy") || "Filter:"}</p>
        <div className="menu-list-segment" role="group" aria-label={t("filterBy") || "Filter"}>
          <button
            type="button"
            className={`menu-list-segment--all ${filter === FILTER_ALL ? "is-active" : ""}`}
            onClick={() => setFilter(FILTER_ALL)}
          >
            {t("all") || "All"} ({menus.length})
          </button>
          <button
            type="button"
            className={`menu-list-segment--on ${filter === FILTER_ENABLED ? "is-active" : ""}`}
            onClick={() => setFilter(FILTER_ENABLED)}
          >
            {t("enabled") || "Enabled"} ({menus.filter((m) => m.isEnabled !== false).length})
          </button>
          <button
            type="button"
            className={`menu-list-segment--off ${filter === FILTER_DISABLED ? "is-active" : ""}`}
            onClick={() => setFilter(FILTER_DISABLED)}
          >
            {t("disabled") || "Disabled"} ({menus.filter((m) => m.isEnabled === false).length})
          </button>
        </div>
      </div>

      <div className="menu-list-toolbar">
        <p className="menu-list-toolbar-label">{t("sortBy") || "Sort:"}</p>
        <div className="menu-list-segment" role="group" aria-label={t("sortBy") || "Sort"}>
          <button
            type="button"
            className={sortMode === SORT_NEWEST ? "is-active" : ""}
            onClick={() => setSortMode(SORT_NEWEST)}
          >
            {t("sortNewest") || "Newest"}
          </button>
          <button
            type="button"
            className={sortMode === SORT_OLDEST ? "is-active" : ""}
            onClick={() => setSortMode(SORT_OLDEST)}
          >
            {t("sortOldest") || "Oldest"}
          </button>
          <button
            type="button"
            className={sortMode === SORT_NAME_ASC ? "is-active" : ""}
            onClick={() => setSortMode(SORT_NAME_ASC)}
          >
            {t("sortNameAsc") || "A → Z"}
          </button>
          <button
            type="button"
            className={sortMode === SORT_NAME_DESC ? "is-active" : ""}
            onClick={() => setSortMode(SORT_NAME_DESC)}
          >
            {t("sortNameDesc") || "Z → A"}
          </button>
          <button
            type="button"
            className={sortMode === SORT_CUSTOM ? "is-active" : ""}
            onClick={() => setSortMode(SORT_CUSTOM)}
          >
            {t("sortCustomOrder") || "Custom order"}
          </button>
        </div>
      </div>

      {canDragReorder ? (
        <p className="menu-list-drag-hint">
          <i className="fas fa-arrows-alt" aria-hidden />
          {t("dragToReorderMenus") || "Drag and drop menus to change the order shown to customers."}
        </p>
      ) : (
        <p className="menu-list-drag-hint">
          <i className="fas fa-info-circle" aria-hidden />
          {t("menuSortHint") ||
            "Showing sorted list. Drag a menu to set the customer order — the view switches to Custom order."}
        </p>
      )}

      <div className="row kitchen-grid" ref={autoScroll.rootRef}>
        {filteredMenus.length === 0 ? (
          <div className="col-12">
            <div className="kitchen-empty" role="alert">
              {t("noDataFound")}
            </div>
          </div>
        ) : (
          filteredMenus.map((item) => {
            const index = item._id;
            return (
            <div
              className={`col-xl-4 col-lg-4 col-md-6 col-sm-12 mb-4 menu-list-card-shell ${
                draggedIndex === index ? "is-dragging" : ""
              } ${dragOverIndex === index ? "is-drop-target" : ""}`}
              key={item._id}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
            >
              <div
                className={`menu-list-card h-100 ${
                  item.isEnabled === false ? "menu-list-card--disabled" : ""
                }`}
              >
                <div className="menu-list-card-body card-body">
                  <div className="menu-list-media">
                    <div className="menu-list-media-toolbar">
                      <span
                        className="menu-list-grip"
                        title={t("dragToReorderMenus") || "Drag to reorder"}
                        aria-hidden
                      >
                        <i className="fas fa-grip-vertical" aria-hidden />
                      </span>
                      <div
                        className="menu-list-actions"
                        onMouseDown={(e) => e.stopPropagation()}
                        onDragStart={(e) => e.preventDefault()}
                      >
                        <Dropdown align="end">
                          <Dropdown.Toggle
                            variant="outline-secondary"
                            className="btn-sm"
                            id={`menu-actions-${item._id}`}
                            title={t("actions") || "Actions"}
                          >
                            <i className="fas fa-ellipsis-v" />
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item
                              onClick={() => navigate(`/edit-menu/${item._id}`)}
                              className="text-warning"
                            >
                              <i className="fas fa-pen me-2 text-warning" />
                              {t("edit")}
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={() => navigate(`/manage-menu/${item._id}`)}
                              className="text-secondary"
                            >
                              <i className="fas fa-sitemap me-2 text-secondary" />
                              {t("manageMenu")}
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={() => handleExportExcel(item)}
                              disabled={exportingMenuId === item._id}
                              className="text-primary"
                            >
                              <i className="fas fa-file-excel me-2 text-success" />
                              {exportingMenuId === item._id
                                ? t("exporting") || "Exporting…"
                                : t("export") || "Export"}
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={() => setShareMenu(item)}
                              className="text-info"
                            >
                              <i className="fas fa-share-alt me-2 text-info" />
                              {t("menuActionShare")}
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={() => handleArchiveMenu(item._id)}
                              className="text-danger"
                            >
                              <i className="fas fa-archive me-2 text-danger" />
                              {t("archive")}
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                    </div>
                    {item.isEnabled === false && (
                      <span
                        className="menu-list-badge-hidden"
                        title={t("menuDisabledTooltip") || "Hidden from customers"}
                      >
                        <i className="fas fa-eye-slash" aria-hidden />
                        {t("disabled") || "Disabled"}
                      </span>
                    )}
                    {menus[0]?._id === item._id && item.isEnabled !== false && (
                      <span
                        className="menu-list-badge-current"
                        title={
                          t("currentMenuTooltip") ||
                          "This menu is shown to customers by default"
                        }
                      >
                        <i className="fas fa-check-circle" aria-hidden />
                        {t("currentMenu") || "Current"}
                      </span>
                    )}
                    <img
                      src={
                        item?.photo?.length > 0
                          ? `${config.UPLOADS_SERVER}/menu/${item.photo}`
                          : imgVide
                      }
                      alt={item.menuDetails[0]?.menuName || "Menu"}
                      draggable={false}
                      onDragStart={(e) => e.preventDefault()}
                    />
                  </div>

                  <div className="menu-list-card-main">
                    <h4 className="menu-list-card-title">
                      {item.menuDetails[0]?.menuName || "No Name"}
                    </h4>
                  </div>

                  <div
                    className="menu-list-visibility-row"
                    onMouseDown={(e) => e.stopPropagation()}
                    onDragStart={(e) => e.preventDefault()}
                  >
                    <div className="menu-list-visibility-text">
                      <span className="menu-list-visibility-label">
                        {t("visibleToCustomers") || "Visible to customers"}
                      </span>
                      <span
                        className={`menu-list-visibility-state ${
                          item.isEnabled === false ? "is-off" : ""
                        }`}
                      >
                        {item.isEnabled === false
                          ? t("disabled") || "Hidden"
                          : t("enabled") || "Visible"}
                      </span>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={item.isEnabled !== false}
                      aria-label={
                        item.isEnabled === false
                          ? t("enableMenu") || "Enable menu"
                          : t("disableMenu") || "Disable menu"
                      }
                      className="menu-list-switch"
                      disabled={togglingMenuId === item._id}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleEnabled(item);
                      }}
                    >
                      <span className="menu-list-switch-knob" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
          })
        )}
      </div>

      <MenuShareModal
        open={Boolean(shareMenu)}
        menu={shareMenu}
        restaurant={restaurantData}
        onClose={() => setShareMenu(null)}
        t={t}
      />
    </>
  );
};

export default MenuList;
